import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { ArrowLeft, ArrowRight, Info, Send, Video } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AtopicNewForm() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    representing: "myself",
    topic: "",
    topic_type: "organization",
    articles: ["", "", ""],
    notes: ""
  });

  const totalSteps = 4;
  const progress = (step / totalSteps) * 100;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleArticleChange = (index, value) => {
    const newArticles = [...formData.articles];
    newArticles[index] = value;
    setFormData(prev => ({ ...prev, articles: newArticles }));
  };

  const nextStep = () => {
    if (step < totalSteps) setStep(step + 1);
  };

  const prevStep = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleSubmit = async (action) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const submitData = {
        ...formData,
        articles: formData.articles.filter(a => a.trim() !== "")
      };

      const response = await axios.post(`${API}/forms/atopicnew`, submitData);
      
      if (response.data.success) {
        toast.success("Form submitted successfully!");
        
        // Navigate to Next Steps page for both options
        navigate('/next-steps', { 
          state: { 
            formId: response.data.form_id,
            formType: "atopicnew",
            formData: submitData,
            action: action
          }
        });
      }
    } catch (error) {
      console.error("Form submission error:", error);
      toast.error("Failed to submit form. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-20 px-6">
      <div className="max-w-2xl mx-auto mb-8">
        <Link to="/" className="inline-flex items-center text-gray-600 hover:text-accent mb-8">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Link>
        
        <div className="flex items-center gap-4 mb-6">
          <img 
            src="https://customer-assets.emergentagent.com/job_df6f992c-82a6-44fb-a88f-450c0ac99b5b/artifacts/cvze4lab_q2y0upqj_75c9mfk0_Square%20Logo%20%28100%20x%20100%29.png" 
            alt="Ethical Wiki" 
            className="w-12 h-12"
            width="48"
            height="48"
          />
          <div>
            <h1 className="font-heading text-3xl text-gray-900">New Page Assessment</h1>
            <p className="text-gray-600">For new Wikipedia page requests</p>
          </div>
        </div>
        
        <Progress value={progress} className="h-1 bg-gray-200" />
        <p className="text-gray-500 text-sm mt-2">Step {step} of {totalSteps}</p>
      </div>

      <div className="max-w-2xl mx-auto">
        <Card className="bg-white border border-gray-200 shadow-sm">
          <CardContent className="p-8">
            {/* Step 1: Contact Info */}
            {step === 1 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <CardHeader className="p-0 mb-8">
                  <CardTitle className="font-heading text-2xl text-gray-900">Contact Information</CardTitle>
                  <CardDescription className="text-gray-600">Tell us how to reach you</CardDescription>
                </CardHeader>
                
                <div className="space-y-6">
                  <div>
                    <Label htmlFor="name" className="text-gray-700">First Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleChange}
                      className="mt-2 bg-white border-gray-300 text-gray-900 focus:border-accent focus:ring-accent"
                      placeholder="John"
                      required
                      data-testid="name-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="email" className="text-gray-700">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="mt-2 bg-white border-gray-300 text-gray-900 focus:border-accent focus:ring-accent"
                      placeholder="john@company.com"
                      required
                      data-testid="email-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="company" className="text-gray-700">Company / Organization</Label>
                    <Input
                      id="company"
                      name="company"
                      type="text"
                      value={formData.company}
                      onChange={handleChange}
                      className="mt-2 bg-white border-gray-300 text-gray-900 focus:border-accent focus:ring-accent"
                      placeholder="Acme Corporation"
                      data-testid="company-input"
                    />
                  </div>

                  <div>
                    <Label className="text-gray-700 mb-3 block">Submission on behalf of *</Label>
                    <RadioGroup 
                      value={formData.representing} 
                      onValueChange={(value) => setFormData(prev => ({ ...prev, representing: value }))}
                      className="space-y-3"
                    >
                      <div className="flex items-center space-x-3 p-3 border border-gray-200 rounded-md hover:bg-gray-50 bg-white">
                        <RadioGroupItem value="myself" id="myself" data-testid="representing-myself" />
                        <Label htmlFor="myself" className="text-gray-700 cursor-pointer flex-1">Myself</Label>
                      </div>
                      <div className="flex items-center space-x-3 p-3 border border-gray-200 rounded-md hover:bg-gray-50 bg-white">
                        <RadioGroupItem value="client" id="client" data-testid="representing-client" />
                        <Label htmlFor="client" className="text-gray-700 cursor-pointer flex-1">My Client</Label>
                      </div>
                      <div className="flex items-center space-x-3 p-3 border border-gray-200 rounded-md hover:bg-gray-50 bg-white">
                        <RadioGroupItem value="employer" id="employer" data-testid="representing-employer" />
                        <Label htmlFor="employer" className="text-gray-700 cursor-pointer flex-1">My Employer</Label>
                      </div>
                    </RadioGroup>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 2: Topic Details */}
            {step === 2 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <CardHeader className="p-0 mb-8">
                  <CardTitle className="font-heading text-2xl text-gray-900">Topic Details</CardTitle>
                  <CardDescription className="text-gray-600">Tell us about the topic you want a Wikipedia page for</CardDescription>
                </CardHeader>
                
                <div className="space-y-6">
                  <div>
                    <Label htmlFor="topic" className="text-gray-700">Topic / Subject *</Label>
                    <Input
                      id="topic"
                      name="topic"
                      type="text"
                      value={formData.topic}
                      onChange={handleChange}
                      className="mt-2 bg-white border-gray-300 text-gray-900 focus:border-accent focus:ring-accent"
                      placeholder="e.g., Company Name, Person Name, Product"
                      required
                      data-testid="topic-input"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-gray-700 mb-3 block">Topic Type *</Label>
                    <RadioGroup 
                      value={formData.topic_type} 
                      onValueChange={(value) => setFormData(prev => ({ ...prev, topic_type: value }))}
                      className="space-y-3"
                    >
                      <div className="flex items-center space-x-3 p-3 border border-gray-200 rounded-md hover:bg-gray-50 bg-white">
                        <RadioGroupItem value="organization" id="organization" data-testid="topic-type-organization" />
                        <Label htmlFor="organization" className="text-gray-700 cursor-pointer flex-1">Organization</Label>
                      </div>
                      <div className="flex items-center space-x-3 p-3 border border-gray-200 rounded-md hover:bg-gray-50 bg-white">
                        <RadioGroupItem value="person" id="person" data-testid="topic-type-person" />
                        <Label htmlFor="person" className="text-gray-700 cursor-pointer flex-1">Person</Label>
                      </div>
                      <div className="flex items-center space-x-3 p-3 border border-gray-200 rounded-md hover:bg-gray-50 bg-white">
                        <RadioGroupItem value="other" id="other" data-testid="topic-type-other" />
                        <Label htmlFor="other" className="text-gray-700 cursor-pointer flex-1">Other</Label>
                      </div>
                    </RadioGroup>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 3: Evidence - Same as AtopicForm but no Desired Changes */}
            {step === 3 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <CardHeader className="p-0 mb-8">
                  <CardTitle className="font-heading text-2xl text-gray-900">Supporting Articles</CardTitle>
                  <CardDescription className="text-gray-600">
                    Provide in-depth profiles in the media that show the topic qualifies for a page.
                  </CardDescription>
                </CardHeader>
                
                <TooltipProvider>
                  <div className="space-y-6">
                    <div className="bg-blue-50 border border-blue-200 p-4 rounded-md mb-6">
                      <h4 className="text-blue-800 font-medium mb-2 flex items-center gap-2">
                        <Info className="w-4 h-4" />
                        What qualifies as a good source?
                      </h4>
                      <ul className="text-blue-700 text-sm space-y-1">
                        <li>• In-depth profile stories where the topic is the primary focus</li>
                        <li>• Written by professional journalists at mainstream national media</li>
                        <li>• Sources that demonstrate independent editorial judgment</li>
                        <li>• NOT press releases, sponsored content, or contributed or guest content</li>
                      </ul>
                    </div>
                    
                    <div>
                      <div className="flex items-center gap-2 mb-4">
                        <Label className="text-gray-700">Article URLs</Label>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="cursor-help"><Info className="w-4 h-4 text-gray-400" /></span>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <p>Provide your 3 best articles that feature significant coverage of the topic.</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      
                      <div className="space-y-4">
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <Label htmlFor="article1" className="text-gray-600 text-sm">Article 1</Label>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="cursor-help"><Info className="w-3 h-3 text-gray-400" /></span>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Your strongest source - ideally a feature article from a major publication</p>
                              </TooltipContent>
                            </Tooltip>
                          </div>
                          <Input
                            id="article1"
                            type="url"
                            value={formData.articles[0]}
                            onChange={(e) => handleArticleChange(0, e.target.value)}
                            className="bg-white border-gray-300 text-gray-900 focus:border-accent focus:ring-accent"
                            placeholder="https://example.com/article-1"
                            data-testid="article-input-0"
                          />
                        </div>
                        
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <Label htmlFor="article2" className="text-gray-600 text-sm">Article 2</Label>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="cursor-help"><Info className="w-3 h-3 text-gray-400" /></span>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Another independent source with substantial coverage</p>
                              </TooltipContent>
                            </Tooltip>
                          </div>
                          <Input
                            id="article2"
                            type="url"
                            value={formData.articles[1]}
                            onChange={(e) => handleArticleChange(1, e.target.value)}
                            className="bg-white border-gray-300 text-gray-900 focus:border-accent focus:ring-accent"
                            placeholder="https://example.com/article-2"
                            data-testid="article-input-1"
                          />
                        </div>
                        
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <Label htmlFor="article3" className="text-gray-600 text-sm">Article 3</Label>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="cursor-help"><Info className="w-3 h-3 text-gray-400" /></span>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Additional supporting coverage from a different source</p>
                              </TooltipContent>
                            </Tooltip>
                          </div>
                          <Input
                            id="article3"
                            type="url"
                            value={formData.articles[2]}
                            onChange={(e) => handleArticleChange(2, e.target.value)}
                            className="bg-white border-gray-300 text-gray-900 focus:border-accent focus:ring-accent"
                            placeholder="https://example.com/article-3"
                            data-testid="article-input-2"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </TooltipProvider>
              </motion.div>
            )}

            {/* Step 4: Final Options */}
            {step === 4 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <CardHeader className="p-0 mb-8">
                  <CardTitle className="font-heading text-2xl text-gray-900">Additional Notes & Next Steps</CardTitle>
                  <CardDescription className="text-gray-600">Any additional information and choose your next step</CardDescription>
                </CardHeader>
                
                <div className="space-y-6">
                  <div>
                    <Label htmlFor="notes" className="text-gray-700">Additional Notes</Label>
                    <Textarea
                      id="notes"
                      name="notes"
                      value={formData.notes}
                      onChange={handleChange}
                      className="mt-2 bg-white border-gray-300 text-gray-900 focus:border-accent focus:ring-accent min-h-[120px]"
                      placeholder="Any other information you'd like to share..."
                      data-testid="notes-input"
                    />
                  </div>
                  
                  <div className="border-t border-gray-200 pt-8">
                    <h3 className="font-heading text-xl text-gray-900 mb-2">Choose Your Next Step</h3>
                    <p className="text-gray-500 text-sm mb-6">Select one of the options below to proceed</p>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-white border-2 border-gray-200 p-6 rounded-lg hover:border-gray-400 transition-colors">
                        <div className="text-center">
                          <Send className="w-8 h-8 text-gray-600 mx-auto mb-4" />
                          <h4 className="font-heading text-lg text-gray-900 mb-2">Free Email Assessment</h4>
                          <p className="text-gray-500 text-sm mb-4">Receive a written assessment via email on whether your topic meets Wikipedia's notability criteria</p>
                          <Button 
                            type="button"
                            onClick={() => handleSubmit("assessment")}
                            className="w-full bg-gray-100 text-gray-800 hover:bg-gray-200 border border-gray-300"
                            disabled={isSubmitting}
                            data-testid="free-assessment-btn"
                          >
                            {isSubmitting ? "Submitting..." : "Get Free Assessment"}
                          </Button>
                        </div>
                      </div>
                      
                      <div className="bg-white border-2 border-accent p-6 rounded-lg hover:border-accent/80 transition-colors">
                        <div className="text-center">
                          <Video className="w-8 h-8 text-accent mx-auto mb-4" />
                          <h4 className="font-heading text-lg text-gray-900 mb-2">Zoom Consultation</h4>
                          <p className="text-gray-500 text-sm mb-2">
                            Includes email assessment plus a 1-hour live video consultation with David King
                          </p>
                          <p className="text-accent font-bold text-lg mb-4">$150</p>
                          <Button 
                            type="button"
                            onClick={() => handleSubmit("consultation")}
                            className="w-full bg-accent text-black hover:bg-accent/90 font-semibold"
                            disabled={isSubmitting}
                            data-testid="paid-consultation-btn"
                          >
                            {isSubmitting ? "Submitting..." : "Book Consultation"}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Navigation */}
            <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
              <Button
                type="button"
                variant="outline"
                onClick={prevStep}
                disabled={step === 1}
                className="border-gray-300 text-gray-700 hover:bg-gray-50"
                data-testid="prev-step-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Previous
              </Button>
              
              {step < 4 && (
                <Button
                  type="button"
                  onClick={nextStep}
                  className="bg-accent text-black hover:bg-accent/90"
                  data-testid="next-step-btn"
                >
                  Next
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
